import { useFormik } from "formik";
import { useMemo } from "react";
import { Button, Col, Form, Row } from "reactstrap";
import Modal from "../../../../components/Common/Modal";
import validationSchema from "./validationSchema";
import Input from "../../../../components/Common/Input";
import Select from "../../../../components/Common/Select";
import { STATES_AND_COUNTRIES } from "../../../../common/constants";
import { MachineApplicationOptions } from "../../../../constants/common";

interface Props {
    isOpen: boolean;
    onToggle: () => void;
    onSubmit: (userData: any) => void;
    isLoading?: boolean;
}

const NewUserModal = ({ isOpen, onToggle, onSubmit, isLoading = false }: Props) => {
    const form = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            company: "",
            machineApplication: "",
            address: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
        },
        validationSchema,
        validateOnMount: true,
        onSubmit: (values) => {
            onSubmit(values);
        },
    });

    const isButtonDisabled = useMemo(() => !!Object.keys(form.errors).length || isLoading, [form, isLoading]);
    const states = useMemo(() => STATES_AND_COUNTRIES.map(({ State, StateCode }) => ({ label: State, value: StateCode })), [STATES_AND_COUNTRIES]);

    const handleClose = () => {
        form.resetForm();
        onToggle();
    };

    return (
        <Modal title="Create New User" isOpen={isOpen} onToggle={handleClose}>
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                    return false;
                }}
            >
                <Row className="mb-3">
                    <Col xs={12} className="font-size-16 fw-bold pb-2">
                        User Information
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="First Name"
                                name="firstName"
                                placeholder="First Name"
                                form={form}
                                value={form.values.firstName}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Last Name"
                                name="lastName"
                                placeholder="Last Name"
                                form={form}
                                value={form.values.lastName}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Email Address"
                                name="email"
                                placeholder="Email Address"
                                form={form}
                                value={form.values.email}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Phone Number"
                                name="phone"
                                placeholder="Phone Number"
                                form={form}
                                value={form.values.phone}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col xs={12} className="font-size-16 fw-bold pb-2">
                        Company Information
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Company"
                                name="company"
                                placeholder="Company"
                                form={form}
                                value={form.values.company}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Select
                                label="Machine Application"
                                name="machineApplication"
                                placeholder="Machine Application"
                                options={MachineApplicationOptions}
                                formik={form}
                                value={MachineApplicationOptions.filter(option => option.value === form.values.machineApplication)}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col xs={12} className="font-size-16 fw-bold pb-2">
                        Address
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Address"
                                name="address"
                                placeholder="Address"
                                form={form}
                                value={form.values.address}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Address 2"
                                name="address2"
                                placeholder="Address 2"
                                form={form}
                                value={form.values.address2}
                            />
                        </div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="mb-3">
                            <Input
                                label="City"
                                name="city"
                                placeholder="City"
                                form={form}
                                value={form.values.city}
                            />
                        </div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="mb-3">
                            <Select
                                name="state"
                                label="State"
                                placeholder="Select State"
                                options={states}
                                formik={form}
                                value={states.filter(option => option.value === form.values.state)}
                            />
                        </div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="mb-3">
                            <Input
                                label="Zipcode"
                                name="zip"
                                placeholder="Zipcode"
                                form={form}
                                value={form.values.zip}
                            />
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col className="text-end">
                        <Button
                            type="button"
                            color="secondary"
                            className="me-2"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={isButtonDisabled}
                        >
                            {isLoading ? "Creating..." : "Create User"}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default NewUserModal;

